import type { HotelResult, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import type { RoomOptionsProps } from '@niaratech/ota-components/src/HotelDetails/RoomOptions/RoomOptions';
import type { RoomTypesGridProps } from '@niaratech/ota-components/src/HotelDetails/RoomTypesGrid/RoomTypesGrid';
import { BREAKPOINT_SM } from '@niaratech/ota-components/src/theme/ota';
import { useImmutableFunction } from '@niaratech/niara-react-components/src';
import _groupBy from 'lodash/groupBy';
import _isEqual from 'lodash/isEqual';
import _uniqBy from 'lodash/uniqBy';
import _omit from 'lodash/omit';
import { useMemo } from 'react';
import { CriteriaForm, useDispatch, useSelector } from '../app/base';
import * as ShoppingCart from '../app/reducers/shoppingCart';
import useShowComissionAndMarkup from './useShowComissionAndMarkup';
import useSignIn from './useSignIn';
const BREAKPOINT_SM_PIXELS = +BREAKPOINT_SM.replace('px', '');
interface RoomOptionParams {
  hotelResult: HotelResult;
  onCTAOverride?: (roomRates: [RoomRate], hotelResult: HotelResult) => void;
  onModifyClick?: () => void;
  isTemporaryItems?: boolean;
  criteria?: CriteriaForm;
}
export const validateAndGenerateSlotTitle = (slots: RoomRate['metasearch']['slots'] = [], selectedSlot: RoomRate['metasearch']['slots'][number]): {
  badge: string | null;
  badgeDescription: string | null;
  badgeColor: string | null;
} => {
  return slots?.length >= 1 || selectedSlot?.ctaAction != 'ADD_TO_CART' ? {
    badge: selectedSlot?.title,
    badgeDescription: selectedSlot?.description,
    badgeColor: selectedSlot?.color
  } : {
    badge: null,
    badgeDescription: null,
    badgeColor: null
  };
};
const useRoomOptions = ({
  hotelResult,
  onCTAOverride,
  onModifyClick,
  isTemporaryItems: _propsIsTemporaryItems,
  criteria
}: RoomOptionParams): Array<RoomOptionsProps['room']> => {
  const [signIn] = useSignIn();

  // se for mobile, ignora isTemporaryItems
  const isTemporaryItems = window.innerWidth <= BREAKPOINT_SM_PIXELS ? false : _propsIsTemporaryItems;
  const {
    b2c,
    maxItemCount,
    priceDisplayMode,
    cartItems,
    temporaryCartItems,
    storefront,
    buyer,
    user,
    isForGroups
  } = useSelector(s => ({
    b2c: s.authentication.b2c,
    maxItemCount: s.shoppingCart?.maxItemCount ?? 1,
    priceDisplayMode: s.hotel?.hotelPostSearchRule?.priceDisplayMode,
    temporaryCartItems: s.shoppingCart.temporaryItems,
    cartItems: s.shoppingCart?.items,
    storefront: s.storefrontConfig?.storefront,
    buyer: s.shoppingCart?.personalData?.buyer,
    user: s.authentication?.user,
    isForGroups: s?.hotel?.isForGroups
  }));

  // vai ter os quartos disponíveis e não disponíveis
  const roomRatesGrouped = useMemo(() => [...((hotelResult?._roomRates /* _roomRates obedecem ao filtro de quartos */ ?? hotelResult?.roomRates /* inclui quartos não filtrados */) || []), ...((hotelResult?._notAvailableRoomRates ?? hotelResult?.notAvailableRoomRates) || [])]?.map(r => [r]), [hotelResult?._notAvailableRoomRates, hotelResult?._roomRates, hotelResult?.notAvailableRoomRates, hotelResult?.roomRates]);

  // agrupado por tipo de quarto
  const roomTypesGroups = useMemo(() => _groupBy(roomRatesGrouped, g => g[0] && g[0].roomType && g[0].roomType.name), [roomRatesGrouped]);
  const showComissionAndMarkup = useShowComissionAndMarkup();
  const roomTypeNamesAndIds = useMemo(() => {
    const items: Array<{
      id?: number;
      name?: string;
    }> = [];
    Object.keys(roomTypesGroups)?.forEach(roomTypeGroup => {
      if (!roomTypeGroup) {
        return;
      }
      items.push({
        id: roomTypesGroups[roomTypeGroup]?.[0]?.[0]?.roomType?.id,
        name: roomTypeGroup
      });
    });
    return items?.filter(Boolean) ?? [];
  }, [roomTypesGroups]);
  const buyerInfo = buyer ?? user;
  const dispatch = useDispatch();
  const selectQuantity = useImmutableFunction((roomRates: [RoomRate], hotelResult: HotelResult, quantity: number, originalRoomRateId?: string, optInRoomRateId?: string) => {
    const loyaltyOptInRoomRateId = originalRoomRateId ? {
      originalId: originalRoomRateId,
      optInId: optInRoomRateId,
      originalCtaAction: 'ADD_TO_CART',
      optInCtaAction: 'ADD_TO_CART_WITH_LATER_OPTIN'
    } as ShoppingCart.CartItem['loyaltyOptInRoomRateId'] : undefined;
    dispatch(ShoppingCart.setHotelRoomRatesQuantity({
      roomRates,
      hotelResult,
      quantity,
      buyer: buyerInfo,
      storefront,
      criteria,
      isTemporaryItems,
      loyaltyOptInRoomRateId,
      isForGroups
    }));
  });
  return useMemo<Array<RoomOptionsProps['room']>>(() => {
    const shoppingCartItems = (temporaryCartItems ?? []).concat(cartItems ?? []);
    const cartItemCount = cartItems?.reduce((acc, i) => acc + i.quantity, 0) ?? 0;
    const temporaryItemCount = temporaryCartItems?.reduce((acc, i) => acc + i.quantity, 0) ?? 0;
    return roomTypeNamesAndIds.map(roomTypeNameAndId => {
      const guestRoomDetail = hotelResult?._hotelDetails?.guestRooms?.filter(r => r?.id == roomTypeNameAndId?.id || r?.description?.toUpperCase().trim() === roomTypeNameAndId?.name?.toUpperCase().trim())[0];
      const sizeMeasurement = guestRoomDetail?.sizeMeasurement === 'sqm' ? 'm²' : 'pés²';
      const size = guestRoomDetail?.size;
      const roomDescription = guestRoomDetail?.medias?.filter(m => m.code == 1)[0]?.texts?.[0]?.Description;
      const maxOccupancy = typeof guestRoomDetail?.maxOccupancy === 'number' ? guestRoomDetail.maxOccupancy : null;
      const views = guestRoomDetail?.views?.map(view => view?.name).length > 1 ? null : guestRoomDetail?.views?.map(view => view?.name);
      const roomAmenities = guestRoomDetail?.amenities && _uniqBy(guestRoomDetail.amenities, a => a.code);
      const images = [];
      const texts = [];
      if (guestRoomDetail && guestRoomDetail.medias) {
        guestRoomDetail.medias.forEach(m => {
          if (m.texts) texts.splice(texts.length, 0, ...m.texts.map(t => t.Description));
          if (m.images) images.splice(images.length, 0, ...m.images.map(x => x.url));
        });
      }
      const roomRates = roomTypesGroups[roomTypeNameAndId?.name].flat(1);
      const priceComposition = roomRates?.map(i => i?.priceComposition?.nights);
      const bedroomCount = roomRates?.map(({
        roomType
      }) => roomType?.bedroomCount)?.[0];
      const rates = roomRates?.map((roomRate: RoomRate): RoomOptionsProps['room']['rates'][number] => {
        let rateAmenities = [];
        if (roomRate?.meal?.breakfast && roomRate?.meal?.lunch && roomRate?.meal?.dinner) {
          rateAmenities.concat('Pensão completa');
        } else if (roomRate?.meal?.breakfast && roomRate?.meal?.lunch || roomRate?.meal?.breakfast && roomRate?.meal?.dinner) {
          rateAmenities.concat('Meia pensão');
        } else if (roomRate?.meal?.breakfast) {
          rateAmenities.concat('Café da manhã');
        }
        rateAmenities = rateAmenities.concat(roomAmenities?.map(a => a));
        const remainingRooms = roomRate?.roomType?.units;
        const [currentQuantityInShoppingCart, currentQuantityInTemporary] = [cartItems, temporaryCartItems].map(c => c?.filter(item => _isEqual(item.roomRateIds, [roomRate.id]))?.reduce((acc, item) => acc + item.quantity, 0) ?? 0);
        const currentQuantity = isTemporaryItems ? currentQuantityInTemporary : currentQuantityInShoppingCart;
        const setValueQtdRooms = (quantity: number) => selectQuantity([roomRate], hotelResult, quantity);

        // quantos quartos sobram
        const remainingRoomsSameSupplier = ShoppingCart.getMaxRoomUnits(roomRate, shoppingCartItems);
        const maxSelectableUnits = Math.min(/* Caso não venha a quantidade de units do fornecedor (ex: Restel), permite adicionar só 1 */
        (isTemporaryItems ? remainingRooms - currentQuantityInShoppingCart : remainingRooms) ?? 1, maxItemCount + (isTemporaryItems ? currentQuantity - cartItemCount - temporaryItemCount : currentQuantity - cartItemCount), remainingRoomsSameSupplier);
        const units = maxSelectableUnits ?? 1;
        const slots: RoomTypesGridProps['slots'] = roomRate?.metasearch?.slots.map(s => {
          const isAddToCartCtaAction = ['ADD_TO_CART', 'ADD_TO_CART_WITH_LATER_OPTIN'].includes(s.ctaAction);
          const [currentQuantitySlotInShoppingCart, currentQuantitySlotInTemporary] = [cartItems, temporaryCartItems].map(c => c?.filter(item => _isEqual(item.roomRateIds, [s?.item?.id ?? roomRate?.id]) && (s?.ctaAction === 'ADD_TO_CART_WITH_LATER_OPTIN' && item.loyaltyOptInRoomRateId?.originalId ? item.loyaltyOptInRoomRateId?.originalId === roomRate?.id && item.loyaltyOptInRoomRateId?.optInId === s?.item?.id : true))?.reduce((acc, item) => acc + item.quantity, 0) ?? 0);
          const currentQuantitySlot = isTemporaryItems ? currentQuantitySlotInTemporary : currentQuantitySlotInShoppingCart;
          const maxSelectableUnitsSlot = Math.min(/* Caso não venha a quantidade de units do fornecedor (ex: Restel), permite adicionar só 1 */
          isTemporaryItems ? remainingRooms - currentQuantitySlotInShoppingCart : remainingRooms ?? 1, maxItemCount + (isTemporaryItems ? currentQuantitySlot - cartItemCount - temporaryItemCount : currentQuantitySlot - cartItemCount), remainingRoomsSameSupplier);
          const units = maxSelectableUnitsSlot ?? 1;
          return {
            ..._omit(s, ['item']),
            highlighted: s.comparison?.price?.value < 0,
            onCTA: isAddToCartCtaAction ? onCTAOverride ? () => {
              onCTAOverride([roomRate], hotelResult);
            } : null : s.ctaAction === 'LOGIN' ? () => signIn?.() : null,
            setValueQtdRooms: isAddToCartCtaAction ? onCTAOverride ? null : roomRate?.metasearch?.slots?.some(slot => slot?.ctaAction === 'ADD_TO_CART_WITH_LATER_OPTIN') ? (quantity: number) => {
              const roomRateSlot = s?.ctaAction === 'ADD_TO_CART_WITH_LATER_OPTIN' ? {
                ..._omit(s?.item, ['metasearch']),
                ..._omit(s, ['id', 'item']),
                metasearch: roomRate?.metasearch
              } : roomRate;
              const optInRoomRateId = roomRate?.metasearch?.slots?.find(slot => slot?.ctaAction === 'ADD_TO_CART_WITH_LATER_OPTIN')?.item?.id;
              selectQuantity([roomRateSlot as unknown as RoomRate], hotelResult, quantity, roomRate?.id, optInRoomRateId);
            } : setValueQtdRooms : null,
            valueQtdRooms: isAddToCartCtaAction ? currentQuantitySlot : null,
            quantityInCart: isTemporaryItems ? currentQuantitySlotInShoppingCart : undefined,
            units: isAddToCartCtaAction ? units : null,
            available: roomRate?.available,
            availabilityRestrictions: roomRate?.availabilityRestrictions,
            roomRate: s?.item || roomRate,
            loyaltyRewards: s?.item?.loyaltyRewards ?? s.loyaltyRewards,
            loyaltyRedeem: s?.item?.loyaltyRedeems?.[0] ?? s.loyaltyRedeems?.[0]
          };
        });
        return {
          hotelName: hotelResult?._hotelByHotelId?.[roomRate.hotelId]?.name,
          id: roomRate.id,
          includedInTheRoom: roomRate?.meal?.name,
          amenities: rateAmenities,
          highlighted: true,
          slots,
          cancelPolicy: roomRate?.cancelPolicy,
          priceComposition: roomRate?.priceComposition,
          offers: roomRate?.offers,
          promotion: roomRate?.promotion,
          loyaltyReward: roomRate?.engineRuleVersion == '1' ? roomRate.loyaltyRewards?.[0] : roomRate?.businessRules?.[0]?.loyaltyRewards?.[0],
          loyaltyRewards: roomRate?.engineRuleVersion == '1' ? roomRate.loyaltyRewards : roomRate?.businessRules?.[0]?.loyaltyRewards,
          loyaltyRedeem: roomRate.loyaltyRedeems?.[0],
          valueQtdRooms: currentQuantity,
          quantityInCart: isTemporaryItems ? currentQuantityInShoppingCart : undefined,
          units,
          paymentOptions: roomRate?.engineRuleVersion == '1' ? roomRate?.paymentOptions : roomRate?.payment?.paymentOptions,
          remainingRooms,
          setValueQtdRooms: !roomRate?.available ? null : onCTAOverride ? null : setValueQtdRooms,
          onCTA: !roomRate?.available ? null : onCTAOverride ? () => {
            onCTAOverride([roomRate], hotelResult);
          } : null,
          availabilityRestrictions: roomRate?.availabilityRestrictions,
          available: roomRate?.available,
          b2c: b2c,
          onModifyClick,
          roomRate: roomRate,
          occupancy: roomRate?.occupancy,
          showComissionAndMarkup,
          credential: roomRate?.credential,
          priceDisplayMode
        } as RoomOptionsProps['room']['rates'][number];
      });
      return {
        area: size,
        sizeMeasurement,
        img: images,
        numberPeoples: maxOccupancy,
        roomTitle: roomTypeNameAndId?.name,
        roomDescr: roomDescription,
        rates: rates,
        priceComposition: priceComposition,
        view: views,
        showComissionAndMarkup,
        bedTypes: guestRoomDetail?.bedTypes ?? [],
        roomId: guestRoomDetail?.id,
        bedroomCount: bedroomCount
      } as RoomOptionsProps['room'];
    })?.filter(room => room.rates?.length > 0);
  }, [temporaryCartItems, cartItems, roomTypeNamesAndIds, hotelResult, roomTypesGroups, showComissionAndMarkup, isTemporaryItems, maxItemCount, onCTAOverride, b2c, onModifyClick, priceDisplayMode, selectQuantity, signIn]);
};
export default useRoomOptions;